import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { TileHeight } from 'shared/components/tiles/tiles.component';
// Enums
import { RiskType } from 'shared/enums/risk-type.enum';
// Interfaces
import { Tile } from 'shared/interfaces/tile.interface';
// Services
import { AlertService } from 'shared/services/alert.service';
import { UnsafeConditionStepperService } from 'shared/services/unsafe-condition-stepper.service';
import { UnsafeConditionService } from 'shared/services/unsafe-condition.service';

@Component({
  selector: 'gw-unsafe-condition-step3',
  templateUrl: './unsafe-condition-step3.component.pug',
  styleUrls: ['./unsafe-condition-step3.component.scss']
})
export class UnsafeConditionStep3Component implements OnInit {
  translation = {
    risk: {
      low: '',
      medium: '',
      increased: '',
      high: ''
    }
  };
  tiles: Array<Tile>;
  tileHeights = TileHeight;
  riskForm: FormGroup;
  submitted = false;
  isSaving: boolean;

  constructor(
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private translateService: TranslateService,
    private unsafeConditionService: UnsafeConditionService,
    private unsafeConditionStepperService: UnsafeConditionStepperService,
    private router: Router,
  ) { }

  async ngOnInit() {
    await this.fetchTranslation();
    this.createTiles();
    this.initForm();
  }

  private initForm(): void {
    const stepData = this.unsafeConditionStepperService.formSteps.step3;
    this.riskForm = this.formBuilder.group({
      risk: [stepData && stepData.risk || '', Validators.required]
    });
  }

  isFieldInvalid(field: AbstractControl): boolean {
    return !field.valid && this.submitted;
  }

  private createTiles(): void {
    this.tiles = [
      {
        id: RiskType.low,
        imageUrl: '/assets/icons/warning-green.svg',
        translationString: this.translation.risk.low
      },
      {
        id: RiskType.medium,
        imageUrl: '/assets/icons/warning-yellow.svg',
        translationString: this.translation.risk.medium
      },
      {
        id: RiskType.increased,
        imageUrl: '/assets/icons/warning-orange.svg',
        translationString: this.translation.risk.increased
      },
      {
        id: RiskType.high,
        imageUrl: '/assets/icons/warning-red.svg',
        translationString: this.translation.risk.high
      },
    ];
  }

  toggleTile(tile: Tile): void {
    if (tile.id !== this.riskForm.controls.risk.value) {
      this.riskForm.controls.risk.setValue(tile.id);
    }
  }

  async submitForm(form: FormGroup): Promise<void> {
    this.submitted = true;

    if (form.valid) {
      this.isSaving = true;
      await this.updateStep(form);
      this.unsafeConditionService.reportUnsafeCondition(
        this.unsafeConditionStepperService.getFormData()
      )
        .then(() => {
          this.alertService.success();
          this.router.navigate(['/esh']);
        })
        .catch(() => {
          this.alertService.error();
          this.isSaving = false;
        });
    }
  }

  updateStep(form: FormGroup): void {
    this.unsafeConditionStepperService.formSteps.step3 = this.unsafeConditionStepperService.convertStep3FormToObject(form);
    this.unsafeConditionStepperService.updateInStorage();
  }

  async fetchTranslation(): Promise<void> {
    const translation = await this.translateService.get([
      'GLOBAL.RISK.LOW',
      'GLOBAL.RISK.MEDIUM',
      'GLOBAL.RISK.INCREASED',
      'GLOBAL.RISK.HIGH',
    ]).toPromise();
    this.translation.risk.low = translation['GLOBAL.RISK.LOW'];
    this.translation.risk.medium = translation['GLOBAL.RISK.MEDIUM'];
    this.translation.risk.increased = translation['GLOBAL.RISK.INCREASED'];
    this.translation.risk.high = translation['GLOBAL.RISK.HIGH'];
  }
}
